type JsonRecord = Record<string, unknown>;

export type InferredProjectPerson = {
	ari?: string;
	jobTitle?: string;
	name: string;
};

export type InferredProjectReferenceIssue = {
	key: string;
	summary?: string;
	status?: string;
	webUrl?: string;
};

export type InferredProjectContext = {
	collaborators: InferredProjectPerson[];
	owners: InferredProjectPerson[];
	referenceIssues: InferredProjectReferenceIssue[];
	summary?: string;
	title?: string;
};

const isRecord = (value: unknown): value is JsonRecord =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const decodeJson = (value: unknown): unknown => {
	let decoded = value;

	for (let attempt = 0; attempt < 2 && typeof decoded === 'string'; attempt++) {
		try {
			decoded = JSON.parse(decoded);
		} catch {
			break;
		}
	}

	return decoded;
};

const getString = (record: JsonRecord, key: string): string | undefined => {
	const value = record[key];
	return typeof value === 'string' && value.trim() ? value : undefined;
};

const findDetailsEntities = (value: unknown, depth = 0): JsonRecord[] | undefined => {
	if (depth > 6) {
		return undefined;
	}

	const decoded = decodeJson(value);
	if (Array.isArray(decoded)) {
		for (const item of decoded) {
			const detailsEntities = findDetailsEntities(item, depth + 1);
			if (detailsEntities) {
				return detailsEntities;
			}
		}
		return undefined;
	}

	if (!isRecord(decoded)) {
		return undefined;
	}

	const facets = decoded.facets;
	if (isRecord(facets) && isRecord(facets.details) && Array.isArray(facets.details.entities)) {
		return facets.details.entities.filter(isRecord);
	}

	for (const nestedValue of Object.values(decoded)) {
		const detailsEntities = findDetailsEntities(nestedValue, depth + 1);
		if (detailsEntities) {
			return detailsEntities;
		}
	}

	return undefined;
};

const getEntityData = (detailsEntities: readonly JsonRecord[], key: string): JsonRecord[] => {
	const detail = detailsEntities.find((entity) => entity.key === key);
	if (!detail || !isRecord(detail.value) || !Array.isArray(detail.value.nodes)) {
		return [];
	}

	return detail.value.nodes.flatMap((node) =>
		isRecord(node) && isRecord(node.data) ? [node.data] : [],
	);
};

const parsePeople = (data: readonly JsonRecord[]): InferredProjectPerson[] =>
	data.flatMap((person) => {
		const name = getString(person, 'name');
		if (!name) {
			return [];
		}

		const extendedProfile = isRecord(person.extendedProfile) ? person.extendedProfile : undefined;
		const jobTitle = extendedProfile ? getString(extendedProfile, 'jobTitle') : undefined;
		const ari = getString(person, 'ari');
		return [{ name, ...(ari ? { ari } : {}), ...(jobTitle ? { jobTitle } : {}) }];
	});

const parseReferenceIssues = (data: readonly JsonRecord[]): InferredProjectReferenceIssue[] =>
	data.flatMap((issue) => {
		const key = getString(issue, 'key');
		if (!key) {
			return [];
		}

		const status = isRecord(issue.status) ? getString(issue.status, 'name') : undefined;
		const summary = getString(issue, 'summary');
		const webUrl = getString(issue, 'webUrl');
		return [
			{
				key,
				...(summary ? { summary } : {}),
				...(status ? { status } : {}),
				...(webUrl ? { webUrl } : {}),
			},
		];
	});

const findMetadata = (
	value: unknown,
	depth = 0,
): Pick<InferredProjectContext, 'summary' | 'title'> => {
	if (depth > 6) {
		return {};
	}

	const decoded = decodeJson(value);
	if (Array.isArray(decoded)) {
		for (const item of decoded) {
			const metadata = findMetadata(item, depth + 1);
			if (metadata.title || metadata.summary) {
				return metadata;
			}
		}
		return {};
	}

	if (!isRecord(decoded)) {
		return {};
	}

	if (isRecord(decoded.metadata)) {
		const title = getString(decoded.metadata, 'title');
		const summary = getString(decoded.metadata, 'summary');
		if (title || summary) {
			return { ...(title ? { title } : {}), ...(summary ? { summary } : {}) };
		}
	}

	for (const nestedValue of Object.values(decoded)) {
		const metadata = findMetadata(nestedValue, depth + 1);
		if (metadata.title || metadata.summary) {
			return metadata;
		}
	}

	return {};
};

export const parseInferredProjectContext = (outputs: unknown): InferredProjectContext => {
	const detailsEntities = findDetailsEntities(outputs) ?? [];
	const metadata = findMetadata(outputs);

	return {
		collaborators: parsePeople(getEntityData(detailsEntities, 'collaborators')),
		owners: parsePeople(getEntityData(detailsEntities, 'owners')),
		referenceIssues: parseReferenceIssues(getEntityData(detailsEntities, 'referenceIssues')),
		...metadata,
	};
};
